import { isDefined } from '@helpers/utils';

import moment from 'moment';
import { maxBy, orderBy } from 'lodash';

import { assignmentFilterType } from '@helpers/constants';

import { VOLUNTARY, HIGHEST } from '@components/assignment-filters/filters-constants';

export const getAssignmentHashKey = (studentId, learningJourneyId) =>
  `studentId#${studentId}_learningJourneyId#${learningJourneyId}`;

export const buildAssignmentHashKeys = ({ studentIds = [], learningJourneyIds = [] }) => {
  const assignmentHashKeys = {};

  studentIds.forEach(studentId => {
    learningJourneyIds.forEach(learningJourneyId => {
      const assignmentHashKey = getAssignmentHashKey(studentId, learningJourneyId);
      assignmentHashKeys[assignmentHashKey] = { studentId, learningJourneyId };
    });
  });

  return assignmentHashKeys;
};

export const destructureAssignmentHashKey = (key) => {
  const returnedValue = {};

  const items = key.split('_');
  items.forEach((item) => {
    const keyValuePair = item.split('#');
    returnedValue[keyValuePair[0]] = keyValuePair[1];
  });

  return returnedValue;
}

export const checkIsAssignmentCompleted = ({ resultStepsTotal, resultStepsCompleted } = {}) =>
  resultStepsTotal > 0 && resultStepsCompleted === resultStepsTotal;

export const getProgressResultText = ({ resultStepsTotal, resultStepsCompleted }) => {
  if (!resultStepsTotal || !resultStepsCompleted) return '';

  return `${resultStepsCompleted}/${resultStepsTotal}`;
};

export const getAccuracyResultText = ({ resultAccuracy }) => (isDefined(resultAccuracy) ? `${resultAccuracy}%` : '');

export const getProgressPercentage = ({ resultStepsCompleted, resultStepsTotal }) => {
  if (!resultStepsTotal || !resultStepsCompleted) return 0;

  return (resultStepsCompleted / resultStepsTotal) * 100;
};

export const getScoreBands = ({
  range,
  currentlyAssigned,
  completed1to20,
  completed21to40,
  completed41to60,
  completed61to80,
  completed81to100
}) => {
  if (!isDefined(range)) return currentlyAssigned;
  if (range > 0 && range <= 20) return completed1to20;
  if (range > 20 && range <= 40) return completed21to40;
  if (range > 40 && range <= 60) return completed41to60;
  if (range > 60 && range <= 80) return completed61to80;
  if (range > 80 && range <= 100) return completed81to100;

  return currentlyAssigned;
};

export default getAssignmentHashKey;

export const checkIsVoluntaryResult = learningJourneyResult =>
  learningJourneyResult?.assignmentType === assignmentFilterType.VOLUNTARY;

export const findVoluntaryAndAssignedLearningJourneyResult = ljResults => {
  const assignedResults = ljResults.filter(
    r => r.assignmentType === assignmentFilterType.VOLUNTARY || r.stepsCompleted === 0
  );

  const sorted = orderBy(assignedResults, ['assignmentType'], ['desc']);
  return sorted[0];
};

export const findVoluntaryLearningJourneyResult = ljResults =>
  ljResults.find(r => r.assignmentType === assignmentFilterType.VOLUNTARY);

export const findLatestAssignedLearningJourneyResult = ljResults => {
  const assignedResults = ljResults.filter(r => r.assignmentType !== assignmentFilterType.VOLUNTARY);

  return maxBy(assignedResults, result => moment(result.assignedDateTime));
};

export const findHighestProgressLearningJourneyResult = ljResults => {
  const sorted = orderBy(ljResults, ['stepsCompleted', 'assignmentType'], ['desc', 'asc']);

  return sorted[0];
};

export const findHighestAccuracyLearningJourneyResult = ljResults => {
  const sorted = orderBy(ljResults, ['accuracy', 'assignmentType'], ['desc', 'asc']);

  return sorted[0];
};

export const getLearningJourneyResultByProgressFilter = (ljResults = [], progressSubFilter) => {
  if (progressSubFilter === VOLUNTARY) {
    return findVoluntaryLearningJourneyResult(ljResults);
  }

  if (progressSubFilter === HIGHEST) {
    return findHighestProgressLearningJourneyResult(ljResults);
  }

  return findLatestAssignedLearningJourneyResult(ljResults);
};

export const getLearningJourneyResultByAccuracyFilter = (ljResults = [], accuracySubFilter) => {
  if (accuracySubFilter === VOLUNTARY) {
    return findVoluntaryLearningJourneyResult(ljResults);
  }

  if (accuracySubFilter === HIGHEST) {
    return findHighestAccuracyLearningJourneyResult(ljResults);
  }

  return findLatestAssignedLearningJourneyResult(ljResults);
};
